import type { Accessor } from 'solid-js';
import type { QueryClient } from '@tanstack/solid-query';

import type { FxWorkspace } from './fx-workspace.types';
import { fxWorkspacesQueryKey } from './fx-workspace-query';

export interface FxWorkspaceListCache {
  cachedWorkspaces(): readonly FxWorkspace[];
  writeCachedWorkspaces(next: readonly FxWorkspace[]): void;
  listContainsWorkspace(workspaceId: string): boolean;
}

export function createFxWorkspaceListCache(options: {
  fetchKey: Accessor<string | undefined>;
  workspaces: Accessor<readonly FxWorkspace[]>;
  queryClient: QueryClient;
}): FxWorkspaceListCache {
  const cachedWorkspaces = (): readonly FxWorkspace[] => {
    const key = options.fetchKey();
    if (key === undefined) {
      return options.workspaces();
    }

    return (
      options.queryClient.getQueryData<readonly FxWorkspace[]>(
        fxWorkspacesQueryKey(key)
      ) ?? options.workspaces()
    );
  };

  const writeCachedWorkspaces = (next: readonly FxWorkspace[]): void => {
    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    options.queryClient.setQueryData(fxWorkspacesQueryKey(key), next);
  };

  const listContainsWorkspace = (workspaceId: string): boolean =>
    options.workspaces().some((workspace) => workspace.id === workspaceId) ||
    cachedWorkspaces().some((workspace) => workspace.id === workspaceId);

  return {
    cachedWorkspaces,
    writeCachedWorkspaces,
    listContainsWorkspace,
  };
}
